import './Analytics.scss';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SideBar from '../../components/SideBar/SideBar';
import TopNav from '../../components/TopNav/TopNav';
import api from '../../services/api';
import Skeleton from '../../components/Skeleton/Skeleton';

const PAGE_SIZE = 30;

const formatTimeAgo = (date) => {
  if (!date) return '';
  const diffMs = Date.now() - new Date(date).getTime();
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(date).toLocaleDateString();
};

const typePill = (type) => {
  const map = {
    favorite: { label: 'Like', bg: 'rgba(231,76,60,0.12)', color: '#E74C3C' },
    save: { label: 'Save', bg: 'rgba(241,196,15,0.12)', color: '#F1C40F' },
    guestlist: { label: 'Guestlist', bg: 'rgba(46,204,113,0.12)', color: '#2ECC71' },
    ticket: { label: 'Ticket', bg: 'rgba(114,41,245,0.16)', color: '#A89AFF' },
    bottle_service: { label: 'Bottle', bg: 'rgba(10,63,214,0.16)', color: '#7AA3FF' },
  };
  const m = map[type] || { label: type || '—', bg: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.6)' };
  return (
    <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 100, background: m.bg, color: m.color, border: `1px solid ${m.color}25` }}>{m.label}</span>
  );
};

const VenueActivity = () => {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filterVenue, setFilterVenue] = useState('all');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/analytics/admin-venue-notifications?page=${page}&records=${PAGE_SIZE}`)
      .then(({ data }) => {
        if (cancelled) return;
        setRows(data.data || []);
        setTotal(data.total || 0);
      })
      .catch((err) => console.error('venue activity fetch failed:', err))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [page]);

  const venues = [...new Set(rows.map(r => r.venue_name).filter(Boolean))].sort();
  const filtered = filterVenue === 'all' ? rows : rows.filter(r => r.venue_name === filterVenue);
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <main>
      <TopNav />
      <div className="main-container">
        <SideBar />
        <article className="page-content">
          <section className="analytics">
            <div className="analytics__header">
              <div>
                <h1 className="analytics__title">Venue activity</h1>
                <p className="analytics__subtitle">Likes, saves, guestlist, ticket + bottle activity across all venues</p>
              </div>
              {venues.length > 0 && (
                <select
                  value={filterVenue}
                  onChange={(e) => setFilterVenue(e.target.value)}
                  style={{ background: '#0a0a0a', border: '1px solid #1a1a1a', color: '#fff', padding: '8px 12px', borderRadius: 10, fontSize: 13 }}
                >
                  <option value="all">All venues</option>
                  {venues.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              )}
            </div>

            {loading ? (
              <Skeleton type="table" />
            ) : filtered.length === 0 ? (
              <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.25)', padding: 60 }}>No activity</div>
            ) : (
              <div className="analytics__chart-section">
                <table style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <th>Venue</th>
                      <th>Activity</th>
                      <th style={{ width: 90 }}>Type</th>
                      <th style={{ width: 110 }}>When</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(r => (
                      <tr key={r._id}>
                        <td style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          {r.venue_image && <img src={r.venue_image} alt="" style={{ width: 32, height: 32, borderRadius: 6, objectFit: 'cover' }} />}
                          <span>{r.venue_name}</span>
                        </td>
                        <td>
                          <div style={{ fontWeight: 600 }}>
                            {r.actor_id ? (
                              <Link to={`/users/${r.actor_id}`} style={{ color: '#A89AFF', textDecoration: 'none' }}>
                                {r.actor_name}
                              </Link>
                            ) : (
                              <span>{r.actor_name}</span>
                            )}
                            {' '}{r.verb} {r.venue_name}
                          </div>
                        </td>
                        <td>{typePill(r.type)}</td>
                        <td style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12 }}>{formatTimeAgo(r.notified_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {totalPages > 1 && (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 16 }}>
                    <button disabled={page === 1} onClick={() => setPage(p => p - 1)} style={{ background: 'transparent', border: '1px solid #1a1a1a', color: '#fff', padding: '6px 12px', borderRadius: 8, cursor: page === 1 ? 'not-allowed' : 'pointer', opacity: page === 1 ? 0.4 : 1 }}>Prev</button>
                    <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13 }}>Page {page} of {totalPages}</span>
                    <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)} style={{ background: 'transparent', border: '1px solid #1a1a1a', color: '#fff', padding: '6px 12px', borderRadius: 8, cursor: page === totalPages ? 'not-allowed' : 'pointer', opacity: page === totalPages ? 0.4 : 1 }}>Next</button>
                  </div>
                )}
              </div>
            )}
          </section>
        </article>
      </div>
    </main>
  );
};

export default VenueActivity;
